// 21/10 : Firefox ne lit pas le HLS. Sans aide, il prenait le MP4 1080p de chaque vignette vidéo (jusqu'à 7 Mbit/s,
// plusieurs à la fois), ce qui ralentissait toute la page ; le lecteur HLS natif de Chrome montait lui aussi au 1080p.
// Hors Safari, hls.js (assets/hls.light.min.js, Apache 2.0) lit le flux en limitant la qualité à la taille de la vignette.
// Safari (Mac, iPhone) garde sa lecture native, dont dépend la lecture automatique sur iPhone.
const HLS_SCRIPT_URL = new URL('hls.light.min.js', import.meta.url).href;
let hlsLibrary;

function loadHlsLibrary() {
  hlsLibrary ??= new Promise((resolve, reject) => {
    if (window.Hls) return resolve(window.Hls);
    const script = document.createElement('script');
    script.src = HLS_SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.Hls ? resolve(window.Hls) : reject(new Error('hls.js unavailable')));
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return hlsLibrary;
}

function needsHlsLibrary() {
  if (/Apple/.test(navigator.vendor)) return false;
  return Boolean(window.MediaSource);
}

class DeferredMedia extends HTMLElement {
  isPlaying = false;
  inViewport = false;
  manuallyPaused = false;
  playRequest = 0;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mediaPoster?.addEventListener('click', this.showDeferredMedia);
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia);
    this.addEventListener('playing', this.onPlaying, true);
    this.addEventListener('pause', this.onPause, true);
    document.addEventListener('visibilitychange', this.syncAutoplay);
    document.addEventListener('otws:video-modal', this.syncAutoplay);
    this.motionPreference.addEventListener('change', this.syncAutoplay);
    navigator.connection?.addEventListener('change', this.syncAutoplay);
    this.autoplayObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        this.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        this.syncAutoplay();
      }
    }, { threshold: [0, 0.15, 0.5], rootMargin: '0px' });
    this.autoplayObserver.observe(this);
    // 21/10 : une vidéo en lecture automatique est insérée un peu avant d'arriver à l'écran,
    // pour qu'elle démarre dès qu'elle devient visible au lieu d'attendre le chargement.
    if (this.hasAttribute('autoplay')) {
      this.preloadObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || !this.autoplayPreferenceAllowsPlayback()) return;
        this.preloadObserver?.disconnect();
        this.loadContent(false);
      }, { rootMargin: '300px 0px' });
      this.preloadObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.autoplayObserver?.disconnect();
    this.preloadObserver?.disconnect();
    this.hls?.destroy();
    this.hls = null;
    this.mediaPoster?.removeEventListener('click', this.showDeferredMedia);
    this.toggleMediaButton?.removeEventListener('click', this.toggleMedia);
    this.removeEventListener('playing', this.onPlaying, true);
    this.removeEventListener('pause', this.onPause, true);
    document.removeEventListener('visibilitychange', this.syncAutoplay);
    document.removeEventListener('otws:video-modal', this.syncAutoplay);
    this.motionPreference?.removeEventListener('change', this.syncAutoplay);
    navigator.connection?.removeEventListener('change', this.syncAutoplay);
    this.inViewport = false;
    this.pauseMedia();
  }

  autoplayPreferenceAllowsPlayback() {
    return !this.motionPreference.matches && navigator.connection?.saveData !== true;
  }

  isSuspended() {
    return document.hidden || Boolean(document.querySelector('.otws-pv__modal:not([hidden])'));
  }

  syncAutoplay = () => {
    if (!this.inViewport || this.isSuspended()) {
      this.pauseMedia();
    } else if (this.hasAttribute('autoplay') && !this.manuallyPaused) {
      if (this.autoplayPreferenceAllowsPlayback()) {
        this.loadContent(false);
        this.playMedia();
      } else {
        this.pauseMedia();
      }
    }
  };

  onPlaying = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    this.isPlaying = true;
    this.manuallyPaused = false;
    this.setAttribute('data-playing', 'true');
    this.mediaPoster?.classList.add('deferred-media__playing');
    this.updatePlayPauseHint(true);
    // Démarrage automatique natif hors écran (chargée en avance) ou derrière le lecteur grand format : on l'arrête.
    if (this.hasAttribute('autoplay') && (!this.inViewport || this.isSuspended())) this.pauseMedia();
  };

  onPause = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    // 21/10 : une pause décidée par le navigateur (iPhone hors écran, appel, autre audio) n'est plus prise
    // pour une pause volontaire ; seul le bouton pause bloque la reprise automatique (toggleMedia).
    this.isPlaying = false;
    this.removeAttribute('data-playing');
    this.updatePlayPauseHint(false);
  };

  updatePlayPauseHint(isPlaying) {
    if (!this.toggleMediaButton) return;
    this.toggleMediaButton.classList.remove('hidden');
    this.toggleMediaButton.querySelector('.icon-play')?.classList.toggle('hidden', isPlaying);
    this.toggleMediaButton.querySelector('.icon-pause')?.classList.toggle('hidden', !isPlaying);
    const label = isPlaying ? this.toggleMediaButton.dataset.pauseLabel : this.toggleMediaButton.dataset.playLabel;
    if (label) this.toggleMediaButton.setAttribute('aria-label', label);
  }

  showDeferredMedia = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.manuallyPaused = false;
    this.loadContent(true);
    this.playMedia();
  };

  loadContent(focus = true) {
    if (this.hasAttribute('data-media-loaded')) return;
    const container = this.querySelector('[data-ref="deferred-media-video"]');
    // 21/10 : importNode crée la vidéo directement dans la page. Clonée depuis le <template>, elle naissait dans un
    // document inerte et, sur iPhone, play() pouvait rester en attente sans jamais démarrer.
    const source = this.querySelector('template')?.content.firstElementChild;
    const content = container?.firstElementChild || (source ? document.importNode(source, true) : null);
    if (!container || !content) return;
    if (content instanceof HTMLVideoElement) {
      // Lecture automatique par les attributs natifs autoplay + muted + playsinline posés avant l'insertion,
      // comme en V2.6 : c'est la voie qu'iOS accepte sans geste de l'utilisateur. Sinon, rien n'est chargé avant le clic.
      const autoplay = this.hasAttribute('autoplay') && !this.manuallyPaused && this.autoplayPreferenceAllowsPlayback();
      content.muted = true;
      content.defaultMuted = true;
      content.setAttribute('muted', '');
      content.playsInline = true;
      content.setAttribute('playsinline', '');
      content.autoplay = autoplay;
      content.preload = autoplay ? 'auto' : 'none';
      content.addEventListener('canplay', this.syncAutoplay);
      const hlsSource = content.querySelector('source[type="application/x-mpegURL"]');
      if (hlsSource && needsHlsLibrary()) {
        const fallbackUrl = content.querySelector('source[type="video/mp4"]')?.src;
        content.querySelectorAll('source').forEach((source) => source.remove());
        this.attachHls(content, hlsSource.src, fallbackUrl);
      }
    }
    this.setAttribute('data-media-loaded', 'true');
    if (!container.contains(content)) container.appendChild(content);
    if (focus && content instanceof HTMLElement) content.focus();
  }

  attachHls(video, hlsUrl, fallbackUrl) {
    const useFallback = () => {
      this.hls?.destroy();
      this.hls = null;
      if (!fallbackUrl || !video.isConnected) return;
      video.src = fallbackUrl;
      video.load();
      this.syncAutoplay();
    };
    loadHlsLibrary().then((Hls) => {
      if (!Hls.isSupported() || !video.isConnected) return useFallback();
      const hls = new Hls({ startLevel: 0, maxBufferLength: 4, maxMaxBufferLength: 8 });
      this.hls = hls;
      // Plus petite qualité dont la hauteur couvre 90 % du lecteur (sans tenir compte de la densité de l'écran) :
      // les vignettes se contentent du 480p au lieu de monter au 1080p.
      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        const target = video.clientHeight * 0.9;
        const index = data.levels.findIndex((level) => level.height >= target);
        hls.autoLevelCapping = index === -1 ? data.levels.length - 1 : index;
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) useFallback();
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
    }).catch(useFallback);
  }

  toggleMedia = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isPlaying) {
      this.manuallyPaused = true;
      this.pauseMedia();
    } else {
      this.manuallyPaused = false;
      this.loadContent(false);
      this.playMedia();
    }
  };

  playMedia() {
    const video = this.querySelector('video');
    if (video) {
      if (!video.paused || this.playPending) return;
      const request = ++this.playRequest;
      this.playPending = true;
      video.play()?.catch(() => {
        if (request === this.playRequest) {
          this.isPlaying = false;
          this.removeAttribute('data-playing');
          this.updatePlayPauseHint(false);
        }
      }).finally(() => {
        if (request === this.playRequest) this.playPending = false;
      });
      return;
    }
    const iframe = this.querySelector('iframe');
    if (!iframe) return;
    iframe.contentWindow?.postMessage(
      iframe.dataset.videoType === 'youtube' || iframe.classList.contains('js-youtube')
        ? '{"event":"command","func":"playVideo","args":""}' : '{"method":"play"}', '*');
    this.isPlaying = true;
    this.mediaPoster?.classList.add('deferred-media__playing');
    this.updatePlayPauseHint(true);
  }

  pauseMedia() {
    this.playPending = false;
    this.playRequest++;
    // Une vidéo déjà à l'arrêt n'est pas mise en pause : pause() annulerait le démarrage automatique natif.
    const video = this.querySelector('video');
    if (video && !video.paused) video.pause();
    const iframe = this.querySelector('iframe');
    iframe?.contentWindow?.postMessage(
      iframe.dataset.videoType === 'youtube' || iframe.classList.contains('js-youtube')
        ? '{"event":"command","func":"pauseVideo","args":""}' : '{"method":"pause"}', '*');
    this.isPlaying = false;
    this.removeAttribute('data-playing');
    if (this.hasAttribute('data-media-loaded')) this.updatePlayPauseHint(false);
  }
}

if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);
